import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Check, AlertCircle, Loader2, ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { getChainConfig } from "@/config/contracts";
import { useTier } from "@/hooks/useTier";
import { can } from "@/lib/tier";
import type { TxStep } from "@/hooks/useTransactionProgress";

// Follows the active chain rather than the build-time env var, so a switched
// session links to the explorer the transaction actually landed on.
const POLYGON_EXPLORER = `${getChainConfig().blockExplorer}/tx/`;

interface TransactionProgressModalProps {
  isOpen: boolean;
  title: string;
  steps: TxStep[];
  onClose: () => void;
  error?: string | null;
  /** Extra content under the steps (e.g. who is helping while this runs). */
  children?: ReactNode;
}

function StepIcon({ status }: { status: TxStep["status"] }) {
  switch (status) {
    case "done":
      return (
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400">
          <Check className="h-4 w-4" />
        </span>
      );
    case "active":
      return (
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-100 text-brand-600 dark:bg-brand-900/30 dark:text-brand-400">
          <Loader2 className="h-4 w-4 animate-spin" />
        </span>
      );
    case "error":
      return (
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400">
          <AlertCircle className="h-4 w-4" />
        </span>
      );
    default:
      return (
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-surface-tertiary text-text-muted">
          <span className="h-2 w-2 rounded-full bg-current" />
        </span>
      );
  }
}

const allDone = (steps: TxStep[]) => steps.length > 0 && steps.every((s) => s.status === "done");
const hasError = (steps: TxStep[]) => steps.some((s) => s.status === "error");

function TransactionProgressModal({
  isOpen,
  title,
  steps,
  onClose,
  error,
  children,
}: TransactionProgressModalProps) {
  const { t } = useTranslation();
  const tier = useTier();

  const done = allDone(steps);
  const errored = hasError(steps) || !!error;

  return (
    <Modal
      open={isOpen}
      onOpenChange={(open) => {
        // Only allow close when done or errored — prevent accidental dismissal mid-flow
        if (!open && (done || errored)) {
          onClose();
        }
      }}
      title={title}
      showClose={done || errored}
      className="max-w-sm"
    >
      <ol className="mt-5 space-y-3" aria-label={t("tx.transactionSteps")}>
        {steps.map((step) => (
          <li
            key={step.id}
            className={cn(
              "flex items-center gap-3 transition-opacity duration-300",
              step.status === "pending" && "opacity-40",
            )}
          >
            <StepIcon status={step.status} />
            <div className="min-w-0 flex-1">
              <p
                className={cn(
                  "text-sm font-medium",
                  step.status === "active" && "text-text-primary",
                  step.status === "done" && "text-text-secondary",
                  step.status === "pending" && "text-text-muted",
                  step.status === "error" && "text-red-600 dark:text-red-400",
                )}
              >
                {step.label}
              </p>
              {step.txHash && can(tier, "onchainDetail") && (
                <a
                  href={`${POLYGON_EXPLORER}${step.txHash}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-0.5 flex items-center gap-1 text-xs text-text-muted hover:text-brand-500"
                >
                  {t("tx.viewOnExplorer")}
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          </li>
        ))}
      </ol>

      {children}

      {error && (
        <p className="mt-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">
          {error}
        </p>
      )}

      {(done || errored) && (
        <div className="mt-5">
          <Button
            variant={errored ? "secondary" : "primary"}
            className="w-full"
            onClick={onClose}
          >
            {/* DEV-838: this button only ever closed the modal. Labelling it
                "Try Again" promised a retry it never performed — and on an
                indeterminate failure a retry is not even safe, because the
                quest may already exist. Say what it does. */}
            {errored ? t("tx.close") : t("tx.done")}
          </Button>
        </div>
      )}
    </Modal>
  );
}

export { TransactionProgressModal };
export type { TransactionProgressModalProps };
